/**
 * Amiy Skincare — GA4 / GTM dataLayer instrumentation
 * Consumes window.__analyticsContext from snippets/analytics-context.liquid
 *
 * Deploy note: remove any duplicate inline amiyTrack script and extra GTM containers
 * from Shopify Admin so only this file + one GTM container fire events.
 */
(function () {
  'use strict';

  if (window.__amiyAnalyticsInitialized) return;
  window.__amiyAnalyticsInitialized = true;

  var STORAGE_PREFIX = 'amiy_analytics_';
  var DEDUPE_PREFIX = STORAGE_PREFIX + 'dedupe_';
  var ATTRIBUTION_COOKIE = 'amiy_attribution';
  var ATTRIBUTION_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;
  var ADD_TO_CART_DEDUPE_MS = 4000;
  var sessionStart = Date.now();
  var pageStart = Date.now();
  var timeOnPageFired = false;
  var timeSpentFired = false;
  var couponFieldFocused = false;
  var pendingCoupon = null;
  var viewCartDebounce = null;
  var listViewFired = false;
  var pageContextFired = false;
  var lastCartFingerprint = '';
  var maxScrollPercent = 0;
  var scrollDepthSent = false;
  var offerAppliedKeys = {};

  function readContext() {
    var el = document.getElementById('analytics-context');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[analytics] Invalid context JSON', e);
      return {};
    }
  }

  var ctx = readContext();
  window.__analyticsContext = ctx;

  function getParam(name) {
    try {
      return new URLSearchParams(window.location.search).get(name) || '';
    } catch (e) {
      return '';
    }
  }

  function store(key, value) {
    if (!value) return;
    try {
      sessionStorage.setItem(STORAGE_PREFIX + key, value);
    } catch (e) {}
  }

  function readStore(key) {
    try {
      return sessionStorage.getItem(STORAGE_PREFIX + key) || '';
    } catch (e) {
      return '';
    }
  }

  function shouldFire(dedupeKey, ttlMs) {
    if (!dedupeKey) return true;
    var now = Date.now();
    try {
      var storageKey = DEDUPE_PREFIX + dedupeKey;
      var last = parseInt(sessionStorage.getItem(storageKey) || '0', 10);
      if (last && now - last < ttlMs) return false;
      sessionStorage.setItem(storageKey, String(now));
    } catch (e) {}
    return true;
  }

  function createEventId(eventName, suffix) {
    return [eventName, suffix || '', Date.now(), Math.random().toString(36).slice(2, 8)].join('_');
  }

  function persistAttributionCookie() {
    var data = {
      page_type: ctx.pageType || '',
      page_path: ctx.pagePath || window.location.pathname,
      template_suffix: ctx.templateSuffix || '',
      language_code: ctx.languageCode || '',
      country_code: ctx.countryCode || '',
      country_name: ctx.countryName || '',
      timezone: (function () {
        try {
          return Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (e) {
          return '';
        }
      })(),
      viewport: window.innerWidth <= 768 ? 'mobile' : 'desktop',
      customer_type: ctx.customerType || 'guest',
      landing_page: readStore('landing_page') || window.location.pathname,
      first_touch_source: readStore('first_touch_source') || '',
      last_touch_source: readStore('last_touch_source') || '',
      utm_source: readStore('utm_source') || '',
      utm_medium: readStore('utm_medium') || '',
      utm_campaign: readStore('utm_campaign') || '',
      utm_content: readStore('utm_content') || '',
      utm_term: readStore('utm_term') || '',
      gclid: readStore('gclid') || '',
      fbclid: readStore('fbclid') || '',
      msclkid: readStore('msclkid') || '',
      ttclid: readStore('ttclid') || '',
      selling_channel: resolveSellingChannel(),
      traffic_source: resolveTrafficSource(),
      is_paid_social: isPaidSocial() ? 'yes' : 'no',
      traffic_story: buildTrafficStory(),
      campaign_label: readStore('utm_campaign') || 'direct_visit'
    };

    try {
      document.cookie =
        ATTRIBUTION_COOKIE +
        '=' +
        encodeURIComponent(JSON.stringify(data)) +
        '; path=/; max-age=' +
        ATTRIBUTION_COOKIE_MAX_AGE +
        '; SameSite=Lax';
    } catch (e) {}
  }

  function normalizeHost(host) {
    return String(host || '')
      .toLowerCase()
      .replace(/^www\./, '');
  }

  function currentHost() {
    return normalizeHost(window.location.hostname);
  }

  function isSameSiteReferrer() {
    if (!document.referrer) return false;
    try {
      return normalizeHost(new URL(document.referrer).hostname) === currentHost();
    } catch (e) {
      return false;
    }
  }

  function hostFromExternalReferrer() {
    if (!document.referrer || isSameSiteReferrer()) return '';
    try {
      return normalizeHost(new URL(document.referrer).hostname);
    } catch (e) {
      return '';
    }
  }

  function isSelfReferralAttribution(source, medium, campaign) {
    var host = currentHost();
    var s = normalizeHost(source);
    if (!s) return false;
    if (s === host) return true;
    if (normalizeHost(medium) === host) return true;
    if (campaign && String(campaign).toLowerCase() === host + '_referral') return true;
    return false;
  }

  function inferMissingAttribution() {
    if (window.__amiyAttribution) return;

    var params = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid', 'msclkid', 'ttclid'];
    params.forEach(function (key) {
      var val = getParam(key);
      if (val) store(key, val);
    });

    var storedSource = readStore('utm_source');
    var storedCampaign = readStore('utm_campaign');

    if (isSelfReferralAttribution(storedSource, readStore('utm_medium'), storedCampaign)) {
      try {
        sessionStorage.removeItem(STORAGE_PREFIX + 'utm_source');
        sessionStorage.removeItem(STORAGE_PREFIX + 'utm_medium');
        sessionStorage.removeItem(STORAGE_PREFIX + 'utm_campaign');
      } catch (e) {}
      storedSource = '';
      storedCampaign = '';
    }

    var hasStoredAttribution = !!(storedSource && storedCampaign);

    var utm_source = getParam('utm_source') || storedSource;
    var utm_medium = getParam('utm_medium') || readStore('utm_medium');
    var utm_campaign = getParam('utm_campaign') || storedCampaign;
    var fbclid = getParam('fbclid') || readStore('fbclid');
    var gclid = getParam('gclid') || readStore('gclid');
    var msclkid = getParam('msclkid') || readStore('msclkid');
    var ttclid = getParam('ttclid') || readStore('ttclid');

    if (!hasStoredAttribution) {
      if (fbclid) {
        if (!utm_source) utm_source = 'facebook';
        if (!utm_medium) utm_medium = 'paid_social';
        if (!utm_campaign) utm_campaign = 'facebook_paid';
      }
      if (gclid) {
        if (!utm_source) utm_source = 'google';
        if (!utm_medium) utm_medium = 'cpc';
        if (!utm_campaign) utm_campaign = 'google_ads';
      }
      if (msclkid) {
        if (!utm_source) utm_source = 'bing';
        if (!utm_medium) utm_medium = 'cpc';
        if (!utm_campaign) utm_campaign = 'bing_ads';
      }
      if (ttclid) {
        if (!utm_source) utm_source = 'tiktok';
        if (!utm_medium) utm_medium = 'paid_social';
        if (!utm_campaign) utm_campaign = 'tiktok_ads';
      }
      if (!utm_source && document.referrer && !isSameSiteReferrer()) {
        var ref = document.referrer.toLowerCase();
        if (ref.indexOf('facebook.com') !== -1 || ref.indexOf('fb.com') !== -1) {
          utm_source = 'facebook';
          utm_medium = utm_medium || 'referral';
          utm_campaign = utm_campaign || 'facebook_referral';
        } else if (ref.indexOf('instagram.com') !== -1) {
          utm_source = 'instagram';
          utm_medium = utm_medium || 'referral';
          utm_campaign = utm_campaign || 'instagram_referral';
        } else if (ref.indexOf('google.') !== -1) {
          utm_source = 'google';
          utm_medium = 'organic';
          utm_campaign = utm_campaign || 'google_organic';
        } else {
          var refHost = hostFromExternalReferrer();
          if (refHost) {
            utm_source = refHost;
            utm_medium = 'referral';
            utm_campaign = utm_campaign || refHost + '_referral';
          }
        }
      }
      if (!utm_source) {
        utm_source = 'direct';
        utm_medium = '(none)';
        utm_campaign = 'direct_visit';
      }

      store('utm_source', utm_source);
      store('utm_medium', utm_medium);
      store('utm_campaign', utm_campaign);
      if (!readStore('first_touch_source')) store('first_touch_source', utm_source);
      store('last_touch_source', utm_source);
    }
  }

  function buildTrafficStory() {
    var boot = window.__amiyAttribution || {};
    var source = boot.utm_source || readStore('utm_source') || 'direct';
    var medium = boot.utm_medium || readStore('utm_medium') || '(none)';
    var campaign = boot.utm_campaign || readStore('utm_campaign') || 'direct_visit';
    return source + ' / ' + medium + ' / ' + campaign;
  }

  function captureAttribution() {
    inferMissingAttribution();

    if (!readStore('landing_page')) {
      store('landing_page', window.location.pathname + window.location.search);
    }

    persistAttributionCookie();
  }

  function isPaidSocial() {
    if (readStore('fbclid')) return true;
    var medium = (readStore('utm_medium') || '').toLowerCase();
    var source = (readStore('utm_source') || '').toLowerCase();
    return (
      medium.indexOf('paid') !== -1 ||
      medium.indexOf('cpc') !== -1 ||
      medium.indexOf('social') !== -1 ||
      source === 'facebook' ||
      source === 'fb' ||
      source === 'meta' ||
      source === 'instagram'
    );
  }

  function resolveTrafficSource() {
    if (readStore('fbclid')) return 'facebook_ads';
    var source = (readStore('utm_source') || '').toLowerCase();
    var medium = (readStore('utm_medium') || '').toLowerCase();
    if (source === 'facebook' || source === 'fb' || source === 'meta' || source === 'instagram') {
      return isPaidSocial() ? 'facebook_ads' : 'facebook_organic';
    }
    if (readStore('gclid') || source === 'google') return 'google_ads';
    if (source) return source;
    if (document.referrer) return 'referral';
    return 'direct';
  }

  function isFreebieSku(sku) {
    return String(sku || '').toUpperCase().trim().indexOf('FREE-') === 0;
  }

  function productTypeFromSku(sku) {
    var s = String(sku || '').toUpperCase().trim();
    if (s.indexOf('S-') === 0) return 'single';
    if (s.indexOf('C-') === 0 || s.indexOf('T-') === 0 || s.indexOf('Q-') === 0) return 'combo';
    return '';
  }

  function resolveSellingChannel() {
    var blob = [
      readStore('utm_source'),
      readStore('utm_medium'),
      readStore('utm_campaign'),
      readStore('utm_content'),
      readStore('utm_term')
    ]
      .join(' ')
      .toLowerCase();

    if (blob.indexOf('doctor') !== -1) return 'doctor';
    if (blob.indexOf('influencer') !== -1) return 'influencer';
    if (blob.indexOf('instagram') !== -1) return 'instagram';
    if (blob.indexOf('facebook') !== -1 || readStore('fbclid')) return 'facebook';
    if (blob.indexOf('meta') !== -1) return 'meta';
    return 'website';
  }

  function enrichItem(item) {
    var sku = item.sku || item.item_id || '';
    return Object.assign({}, item, {
      product_type: item.product_type || productTypeFromSku(sku),
      selling_channel: resolveSellingChannel()
    });
  }

  function filterFreebieItems(items) {
    return (items || [])
      .filter(function (item) {
        return !isFreebieSku(item.sku || item.item_id);
      })
      .map(function (item, index) {
        return enrichItem(Object.assign({}, item, { index: index + 1 }));
      });
  }

  function sanitizeContext() {
    if (ctx.items) ctx.items = filterFreebieItems(ctx.items);
    if (ctx.product && isFreebieSku(ctx.product.sku || ctx.product.item_id)) ctx.product = null;
    if (ctx.product) ctx.product = enrichItem(ctx.product);
    if (ctx.cart && ctx.cart.items) {
      ctx.cart.items = filterFreebieItems(ctx.cart.items);
      ctx.cart.value = ctx.cart.items.reduce(function (sum, item) {
        return sum + (Number(item.price) || 0) * (Number(item.quantity) || 1);
      }, 0);
      ctx.cart.item_count = ctx.cart.items.length;
    }
  }

  function basePayload(extra) {
    var payload = {
      page_type: ctx.pageType || '',
      page_path: ctx.pagePath || window.location.pathname,
      template_suffix: ctx.templateSuffix || '',
      language_code: ctx.languageCode || '',
      country_code: ctx.countryCode || '',
      country_name: ctx.countryName || '',
      timezone: (function () {
        try {
          return Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (e) {
          return '';
        }
      })(),
      viewport: window.innerWidth <= 768 ? 'mobile' : 'desktop',
      customer_type: ctx.customerType || 'guest',
      referrer: document.referrer || '',
      landing_page: readStore('landing_page') || window.location.pathname,
      first_touch_source: readStore('first_touch_source') || '',
      last_touch_source: readStore('last_touch_source') || '',
      utm_source: readStore('utm_source') || '',
      utm_medium: readStore('utm_medium') || '',
      utm_campaign: readStore('utm_campaign') || '',
      utm_content: readStore('utm_content') || '',
      utm_term: readStore('utm_term') || '',
      gclid: readStore('gclid') || '',
      fbclid: readStore('fbclid') || '',
      msclkid: readStore('msclkid') || '',
      ttclid: readStore('ttclid') || '',
      selling_channel: resolveSellingChannel(),
      traffic_source: resolveTrafficSource(),
      is_paid_social: isPaidSocial() ? 'yes' : 'no',
      session_time_spent_seconds: Math.max(0, Math.round((Date.now() - sessionStart) / 1000)),
      traffic_story: buildTrafficStory(),
      campaign_label: readStore('utm_campaign') || (window.__amiyAttribution && window.__amiyAttribution.campaign_label) || 'direct_visit'
    };

    if (extra) {
      Object.keys(extra).forEach(function (k) {
        payload[k] = extra[k];
      });
    }
    return payload;
  }

  function pushEvent(eventName, data) {
    window.dataLayer = window.dataLayer || [];
    var payload = basePayload(data);
    payload.event = eventName;
    if (!payload.event_id) {
      payload.event_id = createEventId(eventName);
    }
    window.dataLayer.push(payload);
  }

  function clearEcommerce() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
  }

  function pushEcommerceEvent(eventName, ecommerce, extra) {
    clearEcommerce();
    var payload = basePayload(extra);
    payload.event = eventName;
    payload.ecommerce = ecommerce;
    if (!payload.event_id) {
      payload.event_id = createEventId(eventName, ecommerce && ecommerce.items && ecommerce.items[0] ? ecommerce.items[0].item_id : '');
    }
    if (ecommerce && typeof ecommerce.value !== 'undefined') {
      payload.amount_spent = Number(ecommerce.value) || 0;
    }
    window.dataLayer.push(payload);
  }

  function centsToAmount(cents) {
    return Math.round(Number(cents || 0)) / 100;
  }

  function shopifyLineToItem(line, index) {
    var sku = line.sku || String(line.variant_id || line.id);
    return enrichItem({
      item_id: sku,
      sku: sku,
      item_name: line.product_title || line.title,
      price: centsToAmount(line.final_price != null ? line.final_price : line.price),
      quantity: line.quantity || 1,
      index: index || 1,
      item_category: line.product_type || '',
      item_brand: line.vendor || '',
      item_variant: line.variant_title || '',
      product_id: line.product_id,
      variant_id: line.variant_id,
      concern_type: ''
    });
  }

  function cartJsToEcommerce(cart) {
    var lines = (cart.items || []).filter(function (line) {
      return !isFreebieSku(line.sku);
    });
    var items = lines.map(function (line, i) {
      return shopifyLineToItem(line, i + 1);
    });
    var value = items.reduce(function (sum, item) {
      return sum + (item.price || 0) * (item.quantity || 1);
    }, 0);
    return {
      currency: cart.currency || ctx.currency || 'USD',
      value: value,
      items: items
    };
  }

  function cartFingerprint(cart) {
    return (cart.items || [])
      .map(function (line) {
        return String(line.variant_id || line.id) + ':' + String(line.quantity || 0);
      })
      .join('|');
  }

  function cartAddResponseToItem(response) {
    var sku = response.sku || String(response.variant_id || response.id);
    return enrichItem({
      item_id: sku,
      sku: sku,
      item_name: response.product_title || response.title,
      price: centsToAmount(response.final_price != null ? response.final_price : response.price),
      quantity: response.quantity || 1,
      index: 1,
      item_category: response.product_type || '',
      item_brand: response.vendor || '',
      item_variant: response.variant_title || '',
      product_id: response.product_id,
      variant_id: response.variant_id,
      concern_type: ''
    });
  }

  function fetchCart() {
    return fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) {
      return r.json();
    });
  }

  function pushPageContext() {
    if (pageContextFired) return;
    pageContextFired = true;
    pushEvent('page_context');
  }

  function pushViewItem() {
    if (!ctx.product || isFreebieSku(ctx.product.sku || ctx.product.item_id)) return;
    pushEcommerceEvent('view_item', {
      currency: ctx.currency,
      value: ctx.product.price,
      items: [ctx.product]
    });
  }

  function pushViewItemList() {
    if (!ctx.items || !ctx.items.length || listViewFired) return;
    listViewFired = true;
    var value = ctx.items.reduce(function (sum, item) {
      return sum + (Number(item.price) || 0);
    }, 0);
    pushEcommerceEvent('view_item_list', {
      currency: ctx.currency,
      value: value,
      items: ctx.items
    }, {
      item_list_id: ctx.itemListId || '',
      item_list_name: ctx.itemListName || ''
    });
  }

  function pushViewSearchResults() {
    if (ctx.pageType !== 'search' || !ctx.searchTerm) return;
    pushEvent('view_search_results', {
      search_term: ctx.searchTerm,
      item_list_id: ctx.itemListId || 'search_results',
      item_list_name: ctx.itemListName || 'Search Results'
    });
    pushViewItemList();
  }

  function pushEvidencePageView() {
    if (!ctx.isEvidencePage) return;
    pushEvent('evidence_page_view');
  }

  function findItemFromGridClick(target) {
    var gridItem = target.closest('[data-grid-item]');
    if (!gridItem) return null;

    var handle = '';
    var url = gridItem.getAttribute('data-url') || '';
    if (url) {
      var match = url.match(/\/products\/([^/?#]+)/);
      if (match) handle = match[1];
    }

    if (ctx.items && ctx.items.length) {
      var fromList = null;
      if (handle) {
        fromList = ctx.items.find(function (item) {
          return item.handle === handle;
        });
      }
      if (fromList) return fromList;
      var link = target.closest('a[href*="/products/"]');
      if (link) {
        var href = link.getAttribute('href') || '';
        var hrefMatch = href.match(/\/products\/([^/?#]+)/);
        if (hrefMatch) {
          handle = hrefMatch[1];
          return (
            ctx.items.find(function (item) {
              return (
                String(item.item_name || '')
                  .toLowerCase()
                  .replace(/\s+/g, '-') === handle.replace(/-/g, ' ') || href.indexOf(String(item.variant_id)) !== -1
              );
            }) || null
          );
        }
      }
    }

    var productLink = target.closest('a[data-product-link]');
    if (productLink) {
      return {
        item_id: handle || productLink.getAttribute('href'),
        item_name: productLink.getAttribute('aria-label') || '',
        price: 0,
        quantity: 1,
        index: 1,
        item_category: '',
        selling_channel: resolveSellingChannel()
      };
    }
    return null;
  }

  function initSelectItem() {
    document.addEventListener('click', function (e) {
      if (!e.target.closest('a[href*="/products/"], [data-product-link]')) return;
      var item = findItemFromGridClick(e.target);
      if (!item || isFreebieSku(item.sku || item.item_id)) return;
      item = enrichItem(item);
      pushEcommerceEvent(
        'select_item',
        {
          currency: ctx.currency,
          value: item.price || 0,
          items: [item]
        },
        {
          item_list_id: ctx.itemListId || '',
          item_list_name: ctx.itemListName || ''
        }
      );
    });
  }

  function initAddToCart() {
    document.addEventListener('theme:product:add', function (e) {
      var response = e.detail && e.detail.response;
      if (!response || response.status) return;
      if (isFreebieSku(response.sku)) return;

      var dedupeKey = 'atc_' + (response.key || response.variant_id || response.id || 'unknown');
      if (!shouldFire(dedupeKey, ADD_TO_CART_DEDUPE_MS)) return;

      var item = cartAddResponseToItem(response);
      pushEcommerceEvent(
        'add_to_cart',
        {
          currency: ctx.currency,
          value: (item.price || 0) * (item.quantity || 1),
          items: [item]
        },
        {
          event_id: createEventId('add_to_cart', dedupeKey),
          shopify_line_key: response.key || ''
        }
      );
    });
  }

  function initRemoveFromCart() {
    document.addEventListener('click', function (e) {
      var removeBtn = e.target.closest('[data-item-remove]');
      if (!removeBtn) return;
      var row = removeBtn.closest('[data-item]');
      if (!row) return;
      var title = row.getAttribute('data-item-title') || '';
      fetchCart().then(function (cart) {
        var line = (cart.items || []).find(function (item) {
          return item.title === title || item.product_title === title;
        });
        if (!line || isFreebieSku(line.sku)) return;
        var gaItem = shopifyLineToItem(line, 1);
        pushEcommerceEvent('remove_from_cart', {
          currency: cart.currency,
          value: gaItem.price * gaItem.quantity,
          items: [gaItem]
        });
      });
    });
  }

  function pushViewCart(cart) {
    if (viewCartDebounce) clearTimeout(viewCartDebounce);
    viewCartDebounce = setTimeout(function () {
      var payload = cart ? cartJsToEcommerce(cart) : null;
      if (!payload) {
        if (ctx.cart) {
          payload = {
            currency: ctx.currency,
            value: ctx.cart.value,
            items: ctx.cart.items
          };
        } else {
          return fetchCart().then(pushViewCart);
        }
      }
      if (!payload.items || !payload.items.length) return;

      var fp = cart ? cartFingerprint(cart) : JSON.stringify(payload.items);
      if (fp === lastCartFingerprint) return;
      lastCartFingerprint = fp;

      if (!shouldFire('view_cart_' + fp, 2000)) return;

      pushEcommerceEvent('view_cart', payload);
    }, 300);
  }

  function initViewCart() {
    if (ctx.pageType === 'cart' && ctx.cart) {
      pushViewCart({ items: ctx.cart.items, currency: ctx.currency, total_price: ctx.cart.value * 100 });
    }
    document.addEventListener('theme:cart:toggle', function () {
      fetchCart().then(pushViewCart);
    });
    document.addEventListener('theme:cart:refresh', function () {
      if (document.querySelector('cart-drawer[open], cart-drawer.is-open, [data-cart-drawer].is-open')) {
        fetchCart().then(pushViewCart);
      }
    });
  }

  function initBeginCheckout() {
    document.addEventListener(
      'click',
      function (e) {
        var checkout = e.target.closest(
          '[data-cart-checkout-button] button[name="checkout"], button[name="checkout"], a[href*="/checkout"]'
        );
        if (!checkout) return;
        if (!shouldFire('begin_checkout', 5000)) return;
        fetchCart().then(function (cart) {
          if (!cart.items || !cart.items.length) return;
          pushEcommerceEvent('begin_checkout', cartJsToEcommerce(cart));
        });
      },
      true
    );
  }

  function initCouponTracking() {
    document.addEventListener('focusin', function (e) {
      if (e.target.matches('[data-discount-input]')) {
        couponFieldFocused = true;
      }
    });

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-apply-discount]');
      if (!btn) return;
      var input = document.querySelector('[data-discount-input]');
      var code = input && input.value ? input.value.trim() : '';
      if (!code) return;
      pendingCoupon = code;
      pushEvent('coupon_entered', { coupon: code });
      pushEvent('coupon_apply_attempt', { coupon: code });
    });

    document.addEventListener('click', function (e) {
      var cartClose = e.target.closest('[data-cart-close], [data-drawer-close]');
      if (!cartClose || !couponFieldFocused || pendingCoupon) return;
      var input = document.querySelector('[data-discount-input]');
      if (input && input.value.trim()) {
        pushEvent('coupon_abandon', { coupon: input.value.trim() });
      }
    });

    if (window.__amiyFetchWrapped) return;
    window.__amiyFetchWrapped = true;

    var originalFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      var isCartUpdate = url.indexOf('/cart/update') !== -1;
      var attemptedCode = pendingCoupon;

      return originalFetch.apply(this, args).then(function (response) {
        if (!isCartUpdate || !attemptedCode) return response;

        return response
          .clone()
          .json()
          .then(function (data) {
            var applied = false;
            if (data && Array.isArray(data.discount_codes)) {
              applied = data.discount_codes.some(function (d) {
                return d.code === attemptedCode && d.applicable;
              });
            }
            if (applied) {
              pushEvent('coupon_success', { coupon: attemptedCode });
            } else {
              pushEvent('coupon_apply_failed', { coupon: attemptedCode });
            }
            pendingCoupon = null;
            checkOfferApplied(data);
            return response;
          })
          .catch(function () {
            return response;
          });
      });
    };
  }

  function checkOfferApplied(cart) {
    if (!cart) return;
    var autoDiscounts = cart.cart_level_discount_applications || [];
    autoDiscounts.forEach(function (discount) {
      if (!discount.title) return;
      if (offerAppliedKeys[discount.title]) return;
      offerAppliedKeys[discount.title] = true;
      pushEvent('offer_applied', { coupon: discount.title, promotion_name: discount.title });
    });
  }

  function getScrollPercent() {
    var doc = document.documentElement;
    var scrollTop = window.pageYOffset || doc.scrollTop;
    var height = Math.max(doc.scrollHeight - window.innerHeight, 1);
    return Math.min(100, Math.round((scrollTop / height) * 100));
  }

  function getScrollBand(max) {
    if (max >= 100) return '100';
    if (max >= 75) return '75';
    if (max >= 50) return '50';
    if (max >= 25) return '25';
    return '0';
  }

  function sendScrollDepth() {
    if (scrollDepthSent || maxScrollPercent < 1) return;
    scrollDepthSent = true;
    var band = getScrollBand(maxScrollPercent);
    pushEvent('scroll_depth', {
      max_percent_scrolled: maxScrollPercent,
      scroll_band: band
    });
  }

  function initScrollDepth() {
    function onScroll() {
      var pct = getScrollPercent();
      if (pct > maxScrollPercent) maxScrollPercent = pct;
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') sendScrollDepth();
    });
    window.addEventListener('pagehide', sendScrollDepth);
  }

  function initTimeTracking() {
    setTimeout(function () {
      if (!timeOnPageFired) {
        timeOnPageFired = true;
        pushEvent('time_on_page_30s');
      }
    }, 30000);

    function sendTimeSpent() {
      if (timeSpentFired) return;
      timeSpentFired = true;
      sendScrollDepth();
      var seconds = Math.round((Date.now() - pageStart) / 1000);
      pushEvent('time_spent', { session_time_spent_seconds: seconds, page_time_spent_seconds: seconds });
    }

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') sendTimeSpent();
    });
    window.addEventListener('pagehide', sendTimeSpent);
  }

  function isFaqAccordionTrigger(trigger) {
    if (!trigger) return false;
    if (trigger.closest('cart-drawer, [data-cart-form], .filter-group, .cart__widget')) return false;
    var section = trigger.closest('[data-section-type]');
    var sectionType = section ? section.getAttribute('data-section-type') || '' : '';
    if (sectionType === 'footer' || sectionType === 'cart-drawer') return false;
    if (sectionType === 'accordion' || sectionType === 'faq') return true;
    if (trigger.closest('.accordion-section, [id*="faq"], [class*="faq"]')) return true;
    if (trigger.closest('.product-tabs, [data-section-type="product"]')) return true;
    return false;
  }

  function initFaqTracking() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-collapsible-trigger], details[data-collapsible] > summary');
      if (!trigger || !isFaqAccordionTrigger(trigger)) return;

      var details = trigger.closest('details[data-collapsible], details.accordion');
      if (details && details.hasAttribute('open')) return;

      var question = trigger.textContent.replace(/\s+/g, ' ').trim();
      var dedupeKey = 'faq_' + question.slice(0, 100);
      if (!shouldFire(dedupeKey, 1500)) return;

      pushEvent('faq_opened', {
        faq_question: question,
        section_type: (trigger.closest('[data-section-type]') || {}).getAttribute
          ? trigger.closest('[data-section-type]').getAttribute('data-section-type') || ''
          : ''
      });
    });
  }

  function isMobileMenuDrawer(el) {
    if (!el) return false;
    var drawer = el.closest ? el.closest('header-drawer') : el;
    if (!drawer || drawer.closest('cart-drawer')) return false;
    if (drawer.closest('mobile-menu, [data-section-type="mobile-menu"]')) return true;
    if (drawer.id && drawer.id.indexOf('mobile-menu') === 0) return true;
    return false;
  }

  function getMobileMenuDepth(el) {
    var panel = el.closest('[data-sliderule]');
    if (!panel) return 0;
    var level = parseInt(panel.getAttribute('data-sliderule'), 10);
    return isNaN(level) ? 0 : level;
  }

  function mobileMenuLinkLabel(el) {
    return (el.getAttribute('aria-label') || el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 120);
  }

  function initMobileMenuTracking() {
    document.addEventListener('theme:drawer:open', function (e) {
      if (!isMobileMenuDrawer(e.target)) return;
      if (!shouldFire('mobile_menu_open', 400)) return;
      pushEvent('mobile_menu_open', {
        menu_id: e.target.id || '',
        open_method: 'hamburger'
      });
    });

    document.addEventListener('theme:drawer:close', function (e) {
      if (!isMobileMenuDrawer(e.target)) return;
      if (!shouldFire('mobile_menu_close', 400)) return;
      pushEvent('mobile_menu_close', {
        menu_id: e.target.id || '',
        close_method: 'drawer'
      });
    });

    document.addEventListener('click', function (e) {
      if (!isMobileMenuDrawer(e.target)) return;

      var submenuOpen = e.target.closest('[data-sliderule-open]');
      if (submenuOpen) {
        pushEvent('mobile_menu_submenu_open', {
          submenu_label: mobileMenuLinkLabel(submenuOpen),
          menu_depth: getMobileMenuDepth(submenuOpen)
        });
        return;
      }

      var submenuClose = e.target.closest('[data-sliderule-close]');
      if (submenuClose) {
        pushEvent('mobile_menu_submenu_close', {
          submenu_label: mobileMenuLinkLabel(submenuClose),
          menu_depth: getMobileMenuDepth(submenuClose)
        });
        return;
      }

      if (e.target.closest('[data-drawer-close], [data-drawer-underlay]')) return;

      var searchToggle = e.target.closest('.mobile-menu__block--search summary, .mobile-menu .navlink--search');
      if (searchToggle) {
        var searchDetails = searchToggle.closest('details');
        if (searchDetails && !searchDetails.hasAttribute('open')) {
          pushEvent('mobile_menu_search_open', { menu_id: (e.target.closest('header-drawer') || {}).id || '' });
        }
        return;
      }

      var link = e.target.closest('a[href]');
      if (!link) return;

      var href = link.getAttribute('href') || '';
      if (!href || href === '#' || href.indexOf('javascript:') === 0) return;

      pushEvent('mobile_menu_link_click', {
        link_text: mobileMenuLinkLabel(link),
        link_url: href,
        link_type: classifyLinkUrl(href),
        menu_depth: getMobileMenuDepth(link),
        is_submenu: getMobileMenuDepth(link) > 0
      });
    });

    document.addEventListener('submit', function (e) {
      if (!isMobileMenuDrawer(e.target)) return;
      var form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.action || form.action.indexOf('/search') === -1) return;
      var input = form.querySelector('input[name="q"], input[type="search"]');
      var term = input ? input.value.trim() : '';
      if (!term) return;
      pushEvent('mobile_menu_search', { search_term: term });
    });
  }

  function isHeaderNavLink(link) {
    if (link.closest('cart-drawer')) return false;
    if (isMobileMenuDrawer(link)) return false;
    if (link.closest('.header__wrapper, header-component, [data-header-wrapper], [data-section-type="header"]')) return true;
    var drawer = link.closest('header-drawer [data-drawer], [data-drawer]');
    if (drawer && !drawer.closest('cart-drawer')) return true;
    return false;
  }

  function isFooterNavLink(link) {
    return !!link.closest('.site-footer-wrapper, footer[data-section-type="footer"], .footer-sections');
  }

  function classifyLinkUrl(href) {
    if (!href) return 'other';
    if (href.indexOf('/products/') !== -1) return 'product';
    if (href.indexOf('/collections/') !== -1) return 'collection';
    if (href.indexOf('/pages/') !== -1) return 'page';
    if (href.indexOf('/blogs/') !== -1) return 'blog';
    if (href === '/' || href.endsWith('/')) return 'home';
    return 'other';
  }

  function initNavLinkTracking() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (!link) return;

      var href = link.getAttribute('href') || '';
      if (!href || href === '#' || href.indexOf('javascript:') === 0) return;
      if (link.hasAttribute('data-cart-toggle') || link.closest('[data-cart-drawer-close]')) return;

      var location = '';
      if (isHeaderNavLink(link)) location = 'header';
      else if (isFooterNavLink(link)) location = 'footer';
      else return;

      var text = (link.getAttribute('aria-label') || link.textContent || '').replace(/\s+/g, ' ').trim();
      pushEvent('nav_link_click', {
        link_location: location,
        link_text: text.slice(0, 120),
        link_url: href,
        link_type: classifyLinkUrl(href)
      });
    });
  }

  function initBeforeAfterTracking() {
    var sections = document.querySelectorAll('.before-after-testimonials, #evidence-testimonials, [id*="evidence"]');
    if (!sections.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          if (!shouldFire('before_after_' + (entry.target.id || 'section'), 86400000)) return;
          pushEvent('before_after_viewed');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.35 }
    );

    sections.forEach(function (section) {
      observer.observe(section);
    });
  }

  function initReviewTracking() {
    var reviewSections = document.querySelectorAll('[data-section-type="reviews"], .reviews--section');
    if (!reviewSections.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          if (!shouldFire('review_viewed_' + (entry.target.id || 'section'), 86400000)) return;
          pushEvent('review_viewed');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.3 }
    );

    reviewSections.forEach(function (section) {
      observer.observe(section);
    });
  }

  function initPromotionTracking() {
    var promoSections = document.querySelectorAll('[data-section-type="slideshow"], [data-section-type="announcement"], .text-promo');
    promoSections.forEach(function (section, index) {
      var id = section.getAttribute('data-section-id') || 'promo-' + (index + 1);
      var name = section.getAttribute('data-section-type') || 'promotion';
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            if (!shouldFire('view_promo_' + id, 86400000)) return;
            pushEvent('view_promotion', {
              promotion_id: id,
              promotion_name: name,
              creative_slot: 'slot_' + (index + 1)
            });
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.5 }
      );
      observer.observe(section);

      section.addEventListener('click', function (e) {
        var link = e.target.closest('a[href]');
        if (!link) return;
        pushEvent('select_promotion', {
          promotion_id: id,
          promotion_name: name,
          creative_name: link.textContent.trim(),
          creative_slot: 'slot_' + (index + 1)
        });
      });
    });
  }

  function initSearchTracking() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.action || form.action.indexOf('/search') === -1) return;
      var input = form.querySelector('input[name="q"], input[type="search"]');
      var term = input ? input.value.trim() : '';
      if (!term) return;
      pushEvent('search', { search_term: term });
    });
  }

  function initNewsletterTracking() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('[data-newsletter-form], form[action*="/contact"]')) return;
      pushEvent('generate_lead', { lead_type: 'newsletter' });
    });
  }

  function initWishlistHooks() {
    document.addEventListener('click', function (e) {
      var addBtn = e.target.closest('[data-wishlist-add], [data-swaction="addToWishlist"], .swym-add-to-wishlist');
      var removeBtn = e.target.closest('[data-wishlist-remove], [data-swaction="removeFromWishlist"], .swym-remove-from-wishlist');
      if (addBtn) {
        pushEvent('add_to_wishlist', {
          item_id: addBtn.getAttribute('data-product-id') || '',
          item_name: addBtn.getAttribute('data-product-title') || ''
        });
      }
      if (removeBtn) {
        pushEvent('remove_from_wishlist', {
          item_id: removeBtn.getAttribute('data-product-id') || '',
          item_name: removeBtn.getAttribute('data-product-title') || ''
        });
      }
    });
  }

  window.amiyTrack = function (eventName, payload) {
    if (!eventName) return;
    if (payload && payload.ecommerce) {
      pushEcommerceEvent(eventName, payload.ecommerce, payload);
    } else {
      pushEvent(eventName, payload || {});
    }
  };

  window.AmiyAnalytics = {
    pushEvent: pushEvent,
    pushEcommerceEvent: pushEcommerceEvent,
    fetchCart: fetchCart,
    shouldFire: shouldFire
  };

  captureAttribution();
  sanitizeContext();
  persistAttributionCookie();

  function boot() {
    pushPageContext();
    pushViewItem();
    pushViewItemList();
    pushViewSearchResults();
    pushEvidencePageView();
    initSelectItem();
    initAddToCart();
    initRemoveFromCart();
    initViewCart();
    initBeginCheckout();
    initCouponTracking();
    initScrollDepth();
    initTimeTracking();
    initFaqTracking();
    initMobileMenuTracking();
    initNavLinkTracking();
    initBeforeAfterTracking();
    initReviewTracking();
    initPromotionTracking();
    initSearchTracking();
    initNewsletterTracking();
    initWishlistHooks();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
